/**
 * TradesHistoryPage Component
 *
 * Displays history of AI investment trades with filter by AI model.
 */

import { FC, useEffect, useState } from 'react';
import { css } from '@emotion/react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { colors, spacing, fontStyles, borderRadius } from '../../styles';
import Page from '../Page';
import Header from '../Header';
import StockLogo from '../StockLogo';
import BottomSheet from '../BottomSheet';
import Button from '../Button';
import { PortfolioSelectionModal } from '../PortfolioSelectionModal';
import FeedbackFooter from '../FeedbackFooter';
import { useWatchlistActions } from '../../hooks/useWatchlistActions';
import {
  InvestingArenaTrade,
  getInvestingArenaActivity,
  AIModelId,
  AI_MODEL_NAMES,
  AI_MODEL_COLORS,
  AI_MODEL_ICONS,
} from '../../apis/investingArena';
import * as stores from '../../stores';
import { FREE_TRADES_LIMIT } from '../../constants/freemium';

// Filter tab type
type FilterTab = 'all' | AIModelId;

// Trade row component
const TradeRow: FC<{
  trade: InvestingArenaTrade;
  onClick: () => void;
  isLocked?: boolean;
  t: (key: string, options?: Record<string, unknown>) => string;
}> = ({ trade, onClick, isLocked, t }) => {
  const modelIcon = AI_MODEL_ICONS[trade.portfolio_id];
  const modelName = AI_MODEL_NAMES[trade.portfolio_id] || trade.portfolio_id;
  const actionColor = trade.action === 'BUY' ? colors.leaf : colors.berry;

  return (
    <div
      css={css`
        display: flex;
        align-items: flex-start;
        padding: ${spacing.deci};
        border-bottom: 1px solid ${colors.fog};
        cursor: pointer;
        transition: background 0.15s ease;
        position: relative;

        &:hover {
          background: ${colors.fog};
        }

        &:last-child {
          border-bottom: none;
        }

        html[data-theme='dark'] & {
          border-color: ${colors.tungsten};

          &:hover {
            background: rgba(80, 78, 75, 0.3);
          }
        }
      `}
      onClick={onClick}
      onKeyDown={(e) => e.key === 'Enter' && onClick()}
      tabIndex={0}
      role="button"
      aria-label={isLocked ? t('trades.unlockWithPro') : t('trades.viewSymbolDetails', { symbol: trade.symbol })}
    >
      <div
        css={css`
          ${isLocked &&
          `
            filter: blur(4px);
            opacity: 0.6;
          `}
        `}
      >
        <StockLogo symbol={trade.symbol} size="medium" />
      </div>
      <div
        css={css`
          flex: 1;
          margin-left: ${spacing.centi};
          min-width: 0;
          ${isLocked &&
          `
            filter: blur(4px);
            opacity: 0.6;
          `}
        `}
      >
        <div
          css={css`
            display: flex;
            align-items: center;
            gap: ${spacing.milli};
            margin-bottom: ${spacing.micro};
          `}
        >
          <span
            css={css`
              ${fontStyles.lion}
              color: var(--text-color);
            `}
          >
            {trade.symbol}
          </span>
          <span
            css={css`
              ${fontStyles.flea}
              color: ${actionColor};
              text-transform: uppercase;
              letter-spacing: 0.5px;
            `}
          >
            {trade.action}
          </span>
          {modelIcon && (
            <div
              css={css`
                width: 18px;
                height: 18px;
                border-radius: 50%;
                background: white;
                display: flex;
                align-items: center;
                justify-content: center;
                border: 1px solid ${colors.cloud};

                html[data-theme='dark'] & {
                  background: #2a2a2a;
                  border-color: ${colors.tungsten};
                }
              `}
              title={modelName}
            >
              <img
                src={modelIcon}
                alt={modelName}
                css={css`
                  width: 11px;
                  height: 11px;
                `}
              />
            </div>
          )}
        </div>
        <div
          css={css`
            ${fontStyles.thinFlea}
            color: ${colors.silver};
            margin-bottom: ${spacing.micro};

            html[data-theme='dark'] & {
              color: ${colors.cloud};
            }
          `}
        >
          ${trade.fill_price.toFixed(2)} · {trade.relative_time}
        </div>
        <p
          css={css`
            ${fontStyles.cat}
            color: var(--text-color);
            margin: 0;
            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
            overflow: hidden;
          `}
        >
          {trade.rationale}
        </p>
      </div>

      {/* Unlock with Pro badge for locked items */}
      {isLocked && (
        <div
          css={css`
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            background: ${colors.petrolBlue};
            color: ${colors.sugar};
            padding: 4px 12px;
            border-radius: 4px;
            ${fontStyles.flea}
            font-weight: 500;
            white-space: nowrap;
            z-index: 2;
          `}
        >
          {t('trades.unlockWithPro')}
        </div>
      )}
    </div>
  );
};

const skeletonPulse = css`
  @keyframes pulse {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0.5;
    }
  }
  animation: pulse 1.5s ease-in-out infinite;
`;

// Skeleton loader
const TradesHistorySkeleton: FC = () => {
  return (
    <div>
      {/* Trade rows skeleton */}
      <div
        css={css`
          background: ${colors.snow};
          border: 1px solid ${colors.fog};
          border-radius: ${borderRadius};
          overflow: hidden;

          html[data-theme='dark'] & {
            background: #1a1919;
            border-color: ${colors.tungsten};
          }
        `}
      >
        {[1, 2, 3, 4, 5].map((i) => (
          <div
            key={i}
            css={css`
              display: flex;
              padding: ${spacing.deci};
              border-bottom: 1px solid ${colors.fog};

              &:last-child {
                border-bottom: none;
              }

              html[data-theme='dark'] & {
                border-color: ${colors.tungsten};
              }
            `}
          >
            <div
              css={css`
                width: 32px;
                height: 32px;
                border-radius: 20%;
                background: ${colors.cloud};
                ${skeletonPulse}

                html[data-theme='dark'] & {
                  background: ${colors.tungsten};
                }
              `}
            />
            <div
              css={css`
                flex: 1;
                margin-left: ${spacing.centi};
              `}
            >
              <div
                css={css`
                  width: 120px;
                  height: 16px;
                  background: ${colors.cloud};
                  border-radius: 4px;
                  margin-bottom: ${spacing.centi};
                  ${skeletonPulse}

                  html[data-theme='dark'] & {
                    background: ${colors.tungsten};
                  }
                `}
              />
              <div
                css={css`
                  width: 100%;
                  height: 32px;
                  background: ${colors.cloud};
                  border-radius: 4px;
                  ${skeletonPulse}

                  html[data-theme='dark'] & {
                    background: ${colors.tungsten};
                  }
                `}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const TradesHistoryPage: FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const [isOpen, setIsOpen] = useState(false);
  const [activeFilter, setActiveFilter] = useState<FilterTab>('all');
  const [trades, setTrades] = useState<InvestingArenaTrade[]>([]);
  const [selectedTrade, setSelectedTrade] = useState<InvestingArenaTrade | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const purchaserInfo = stores.Purchase.useState((s) => s.purchaserInfo);
  const hasActiveSubscription = !!purchaserInfo?.entitlements?.active[stores.entitlementIdentifier]?.isActive;

  // Watchlist actions from shared hook
  const {
    isWatchlistModalOpen,
    closeWatchlistModal,
    plaidPortfolioIds,
    isInWatchlist,
    handleAddToWatchlist,
    handlePortfolioSubmit,
  } = useWatchlistActions();

  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      setIsLoading(true);
      try {
        const tradesData = await getInvestingArenaActivity(activeFilter === 'all' ? undefined : activeFilter, 50);
        if (isMounted) {
          setTrades(tradesData);
        }
      } catch (error) {
        console.error('[TradesHistoryPage] Failed to fetch data:', error);
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, [activeFilter]);

  const handleTradeClick = (trade: InvestingArenaTrade) => {
    setSelectedTrade(trade);
    setIsOpen(true);
  };

  const handleClose = () => {
    setIsOpen(false);
    closeWatchlistModal(); // Also close portfolio modal if open
  };

  const handleViewStock = () => {
    if (selectedTrade) {
      setIsOpen(false);
      closeWatchlistModal(); // Also close portfolio modal if open
      history.push(`/symbol/${selectedTrade.symbol}`);
    }
  };

  const onAddToWatchlist = () => {
    if (selectedTrade) {
      handleAddToWatchlist(selectedTrade.symbol);
    }
  };

  const onPortfolioSubmit = (selectedIds: string[]) => {
    if (selectedTrade) {
      handlePortfolioSubmit(selectedTrade.symbol, selectedIds);
    }
  };

  const filterTabs: { value: FilterTab; label: string; icon?: string }[] = [
    { value: 'all', label: t('trades.allModels') },
    { value: 'claude', label: 'Claude', icon: AI_MODEL_ICONS.claude },
    { value: 'openai', label: 'ChatGPT', icon: AI_MODEL_ICONS.openai },
    { value: 'gemini', label: 'Gemini', icon: AI_MODEL_ICONS.gemini },
  ];

  return (
    <Page showNavigationBar>
      <Header headingText={t('trades.allTrades')} onBack={() => history.goBack()} variant="left-aligned" />

      <div
        css={css`
          max-width: 600px;
          margin: 0 auto;
          padding-top: ${spacing.centi};
        `}
      >
        {/* Filter tabs */}
        <div
          css={css`
            display: flex;
            gap: ${spacing.milli};
            padding: ${spacing.micro};
            margin-bottom: ${spacing.centi};
            background: ${colors.fog};
            border-radius: 12px;
            border: 0.5px solid ${colors.liquorice}20;
            width: 100%;
            max-width: 600px;

            html[data-theme='dark'] & {
              background: rgba(80, 78, 75, 0.3);
              border-color: ${colors.cloud}30;
            }
          `}
        >
          {filterTabs.map(({ value, label, icon }) => (
            <button
              key={value}
              onClick={() => setActiveFilter(value)}
              css={css`
                ${fontStyles.mouse}
                flex: 1;
                display: flex;
                align-items: center;
                justify-content: center;
                gap: ${spacing.milli};
                padding: ${spacing.centi} ${spacing.milli};
                border: none;
                border-radius: 10px;
                cursor: pointer;
                transition: all 0.15s ease;
                white-space: nowrap;
                background: ${activeFilter === value ? 'white' : 'transparent'};
                color: ${activeFilter === value ? 'var(--text-color)' : colors.silver};
                box-shadow: ${activeFilter === value ? '0 1px 3px rgba(0, 0, 0, 0.08)' : 'none'};
                font-weight: ${activeFilter === value ? '500' : '400'};

                &:hover {
                  color: var(--text-color);
                }

                html[data-theme='dark'] & {
                  background: ${activeFilter === value ? colors.liquorice : 'transparent'};
                  color: ${activeFilter === value ? 'var(--text-color)' : colors.cloud};
                }
              `}
            >
              {icon && (
                <img
                  src={icon}
                  alt=""
                  css={css`
                    width: 14px;
                    height: 14px;
                  `}
                />
              )}
              {label}
            </button>
          ))}
        </div>

        {isLoading ? (
          <TradesHistorySkeleton />
        ) : (
          <>
            {/* Trades list */}
            <div
              css={css`
                background: ${colors.snow};
                border: 1px solid ${colors.fog};
                border-radius: ${borderRadius};
                overflow: hidden;

                html[data-theme='dark'] & {
                  background: #1a1919;
                  border-color: ${colors.tungsten};
                }
              `}
            >
              {trades.length > 0 ? (
                trades.map((trade, index) => {
                  const isLocked = !hasActiveSubscription && index >= FREE_TRADES_LIMIT;
                  return (
                    <TradeRow
                      key={`${trade.order_id}-${index}`}
                      trade={trade}
                      isLocked={isLocked}
                      t={t}
                      onClick={() => {
                        if (isLocked) {
                          stores.showPaymentModalByPreference('all_trades_page');
                        } else {
                          handleTradeClick(trade);
                        }
                      }}
                    />
                  );
                })
              ) : (
                <div
                  css={css`
                    padding: ${spacing.regular};
                    text-align: center;
                    color: ${colors.silver};
                    ${fontStyles.dog}

                    html[data-theme='dark'] & {
                      color: ${colors.tungsten};
                    }
                  `}
                >
                  {t('trades.noTradesFound')}
                </div>
              )}
            </div>
          </>
        )}

        <FeedbackFooter />
      </div>

      {/* Bottom sheet for trade details */}
      <BottomSheet isOpen={isOpen} onClose={handleClose}>
        {selectedTrade && (
          <>
            {/* Custom header with stock info + close button */}
            <div
              css={css`
                display: flex;
                align-items: center;
                justify-content: space-between;
                padding-bottom: ${spacing.deci};
                border-bottom: 1px solid ${colors.fog};
                margin-bottom: ${spacing.deci};

                html[data-theme='dark'] & {
                  border-color: ${colors.tungsten};
                }
              `}
            >
              <div
                css={css`
                  display: flex;
                  align-items: center;
                  gap: ${spacing.centi};
                `}
              >
                <StockLogo symbol={selectedTrade.symbol} size="large" />
                <div>
                  <div
                    css={css`
                      display: flex;
                      align-items: center;
                      gap: ${spacing.milli};
                    `}
                  >
                    <span
                      css={css`
                        ${fontStyles.lion}
                        color: var(--text-color);
                      `}
                    >
                      {selectedTrade.symbol}
                    </span>
                    <span
                      css={css`
                        ${fontStyles.flea}
                        color: ${selectedTrade.action === 'BUY' ? colors.leaf : colors.berry};
                        text-transform: uppercase;
                      `}
                    >
                      {selectedTrade.action}
                    </span>
                  </div>
                  <div
                    css={css`
                      ${fontStyles.mouse}
                      color: ${colors.silver};
                      display: flex;
                      align-items: center;
                      gap: ${spacing.micro};
                    `}
                  >
                    <div
                      css={css`
                        width: 16px;
                        height: 16px;
                        border-radius: 50%;
                        background: white;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        border: 1px solid ${colors.cloud};

                        html[data-theme='dark'] & {
                          background: #2a2a2a;
                          border-color: ${colors.tungsten};
                        }
                      `}
                    >
                      <img
                        src={AI_MODEL_ICONS[selectedTrade.portfolio_id]}
                        alt=""
                        css={css`
                          width: 10px;
                          height: 10px;
                        `}
                      />
                    </div>
                    <span
                      css={css`
                        color: ${AI_MODEL_COLORS[selectedTrade.portfolio_id]};
                      `}
                    >
                      {AI_MODEL_NAMES[selectedTrade.portfolio_id]}
                    </span>
                    <span>·</span>
                    <span>${selectedTrade.fill_price.toFixed(2)}</span>
                    {selectedTrade.relative_time && (
                      <>
                        <span>·</span>
                        <span>{selectedTrade.relative_time}</span>
                      </>
                    )}
                  </div>
                </div>
              </div>
              <button
                css={css`
                  background: none;
                  border: none;
                  padding: 8px;
                  cursor: pointer;
                  color: ${colors.tungsten};
                  font-size: 24px;
                  line-height: 1;

                  &:hover {
                    color: ${colors.liquorice};
                  }

                  html[data-theme='dark'] & {
                    color: ${colors.cloud};
                    &:hover {
                      color: ${colors.sugar};
                    }
                  }
                `}
                onClick={handleClose}
                aria-label={t('common.close')}
              >
                ×
              </button>
            </div>

            {/* Rationale */}
            {selectedTrade.rationale && (
              <div
                css={css`
                  margin-bottom: ${spacing.centi};
                `}
              >
                <h4
                  css={css`
                    ${fontStyles.mouse}
                    color: ${colors.silver};
                    margin: 0 0 ${spacing.micro} 0;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                  `}
                >
                  {t('trades.rationale')}
                </h4>
                <p
                  css={css`
                    ${fontStyles.cat}
                    color: var(--text-color);
                    margin: 0;
                    line-height: 1.5;
                  `}
                >
                  {selectedTrade.rationale}
                </p>
              </div>
            )}

            {/* Catalysts */}
            {selectedTrade.catalysts && selectedTrade.catalysts.length > 0 && (
              <div
                css={css`
                  margin-bottom: ${spacing.centi};
                `}
              >
                <h4
                  css={css`
                    ${fontStyles.mouse}
                    color: ${colors.silver};
                    margin: 0 0 ${spacing.micro} 0;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                  `}
                >
                  {t('trades.catalysts')}
                </h4>
                <ul
                  css={css`
                    ${fontStyles.cat}
                    color: var(--text-color);
                    margin: 0;
                    padding-left: ${spacing.deci};
                  `}
                >
                  {selectedTrade.catalysts.map((catalyst, i) => (
                    <li key={i}>{catalyst}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Risks */}
            {selectedTrade.risks && selectedTrade.risks.length > 0 && (
              <div>
                <h4
                  css={css`
                    ${fontStyles.mouse}
                    color: ${colors.silver};
                    margin: 0 0 ${spacing.micro} 0;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                  `}
                >
                  {t('trades.risks')}
                </h4>
                <ul
                  css={css`
                    ${fontStyles.cat}
                    color: var(--text-color);
                    margin: 0;
                    padding-left: ${spacing.deci};
                  `}
                >
                  {selectedTrade.risks.map((risk, i) => (
                    <li key={i}>{risk}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Stacked action buttons */}
            <div
              css={css`
                display: flex;
                flex-direction: column;
                gap: ${spacing.deci};
                margin-top: ${spacing.regular};
              `}
            >
              <Button variant="primary" block onClick={handleViewStock}>
                {t('trades.viewSymbol', { symbol: selectedTrade.symbol })}
              </Button>
              <Button variant="secondary" block onClick={onAddToWatchlist}>
                {isInWatchlist(selectedTrade.symbol) ? t('trades.inWatchlist') : t('trades.addToWatchlist')}
              </Button>
            </div>
          </>
        )}
      </BottomSheet>

      {/* Portfolio selection modal for multi-portfolio users */}
      <PortfolioSelectionModal
        isOpen={isWatchlistModalOpen}
        onClose={closeWatchlistModal}
        onSubmit={onPortfolioSubmit}
        title={t('trades.addToPortfolio')}
        symbol={selectedTrade?.symbol}
        excludePortfolioIds={plaidPortfolioIds}
      />
    </Page>
  );
};

export default TradesHistoryPage;
